import type { GapDraftView } from '@momentum/types';

import type { DraftEditForm } from '../hooks/useDraftEditForm';
import type { EditableGapFieldState } from '../lib/draft-edit';
import { pluralize } from '../lib/format';
import { EditableField } from './EditableField';

/** The fields in document order, split under the reader's own section names. */
function groupFields(
  fields: EditableGapFieldState[]
): { group: string; fields: EditableGapFieldState[] }[] {
  const groups: { group: string; fields: EditableGapFieldState[] }[] = [];
  for (const field of fields) {
    const openGroup = groups.at(-1);
    if (openGroup?.group === field.group) openGroup.fields.push(field);
    else groups.push({ group: field.group, fields: [field] });
  }
  return groups;
}

/**
 * The reader in edit mode — the product layer as a form.
 *
 * It replaces the document rather than turning it into inline inputs. The
 * reader's job is to be read in a fixed order at a glance; editing it is a
 * different job, done a field at a time, and it needs things the document has
 * no room for: what the field is called in the data, whether an agent ever
 * wrote it, and a place to say why the change was made.
 *
 * Only `gap.*` is here. `source` is the run's own record, frozen at insert, and
 * a reviewer improving the product copy is not amending what the run found.
 *
 * Fields are seeded from `gap` alone, so a field the reader shows as "from run"
 * opens EMPTY here. That is not a bug: leaving it empty writes nothing, and
 * typing into it is a human authoring the product field, which is exactly what
 * it would be.
 */
export function ReaderEditForm({
  draft,
  form,
}: {
  draft: GapDraftView;
  form: DraftEditForm;
}) {
  const groups = groupFields(form.fields);

  return (
    <article className="gr-doc">
      <header className="gr-head">
        <h1 className="gr-h1">Editing the product layer</h1>
        <span className="gr-h1-tag" data-tone="cyan">
          gap.* only · source is frozen
        </span>
      </header>

      <div className="gr-note gr-stack-top" data-tone="cyan">
        <div>
          <strong>Every change is recorded.</strong> What the agents wrote is
          kept beside what you replaced it with, so the pass can be read back
          later when the prompts are revised — the Edits tab in the rail is that
          record. A reason is optional and belongs to the one change it
          explains.
        </div>
      </div>

      {groups.map((fieldGroup) => (
        <section key={fieldGroup.group}>
          <div className="gr-rule">
            <span>{fieldGroup.group}</span>
            <i />
          </div>
          {fieldGroup.fields.map((field) => (
            <EditableField
              key={field.path}
              field={field}
              onValueChange={(value) => form.setValue(field.path, value)}
              onReasonChange={(reason) => form.setReason(field.path, reason)}
            />
          ))}
        </section>
      ))}

      {form.fields.length === 0 && (
        <div className="gr-empty">
          <b>Nothing to edit</b>
          {draft.scopeName} has no editable product fields on this draft.
        </div>
      )}
    </article>
  );
}

/**
 * The save bar, in the verdict bar's place while the form is open.
 *
 * It counts changes rather than touched fields: a score box the reviewer
 * cleared is not a change — there is no value that unsets a score — and
 * promising to save something that will not be sent is worse than the count
 * looking low.
 */
export function ReaderEditBar({ form }: { form: DraftEditForm }) {
  const isBlocked = form.changeCount === 0 || form.hasProblem || form.isSaving;

  return (
    <div className="gr-actionbar">
      <button
        className="gr-btn"
        type="button"
        data-role="keep"
        disabled={isBlocked}
        onClick={form.save}
      >
        {form.isSaving ? 'Saving…' : 'Save edits'}{' '}
        <span className="gr-kbd">⌘↵</span>
      </button>
      <button className="gr-btn-ghost" type="button" onClick={form.cancel}>
        Cancel
      </button>

      <div className="gr-actionbar-group">
        <i className="gr-actionbar-sep" />
        <span className="gr-count">
          {form.hasProblem
            ? 'A field cannot be saved as typed'
            : `${pluralize(form.changeCount, 'change')} to save`}
        </span>
      </div>
    </div>
  );
}
